import { useQuery } from '@tanstack/vue-query'
import { apiClient } from '@/apis/instances/api-client'
import { checkApiResponse } from '@/apis/instances/check-api-response'
import { ApiV2ReservationblockElFindParams } from '@/apis/swaggers/v2/v2-swagger'
import { QUERY_KEY } from '@/apis/swaggers/swagger-docs'
import { QueryComposableParams } from '@/types/query-options'
import { resolveMaybeGetter } from '@/util/resolve-maybe-getter'

type QueryParams = QueryComposableParams<ApiV2ReservationblockElFindParams, unknown>

const fetchReservationBlockList = async (params: ApiV2ReservationblockElFindParams, signal?: AbortSignal) => {
  const { data } = await apiClient.v2.apiV2ReservationblockElFind(params, { signal });
  return checkApiResponse(data);
}

export const v2ReservationBlockKeys = {
  all: [QUERY_KEY.v2apiV2ReservationblockElFind] as const,
  list: (params: ApiV2ReservationblockElFindParams) => [...v2ReservationBlockKeys.all, params] as const,
}

/** 명령형(await) 호출용 쿼리 정의.
 * queryFn 이 TanStack 의 AbortSignal 을 axios 로 전달하므로,
 * 진행 중 요청 취소는 `queryClient.cancelQueries({ queryKey: v2ReservationBlockKeys.all })` 로 한다
 * (레거시 axios cancelToken 흐름의 대체) */
export const v2ReservationBlockFindQueryOptions = (params: ApiV2ReservationblockElFindParams) => ({
  queryKey: v2ReservationBlockKeys.list(params),
  queryFn: ({ signal }: { signal?: AbortSignal }) => fetchReservationBlockList(params, signal),
  // 날짜별 데이터 — FCM invalidation(타 사용자 변경)과 mutation invalidation(내 변경)이 커버하므로 5분 캐시
  staleTime: 5 * 60 * 1000,
  gcTime: 5 * 60 * 1000,
})

export const useV2ReservationBlockFindQuery = ({ params, options }: QueryParams) => {
  return useQuery(() => ({
    queryKey: v2ReservationBlockKeys.list(resolveMaybeGetter(params)),
    queryFn: ({ signal }) => fetchReservationBlockList(resolveMaybeGetter(params), signal),
    ...resolveMaybeGetter(options ?? {}),
  }))
}
